import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { composeFileName, downloadFile, isEmptyList } from '../../utils'
import {
    DeviceSerialQrCodeModal,
    Loading,
    PageTitle,
    Tooltip
} from '../root/components/common'
import * as actions from './actions'
import { DevicesTable } from './components/devices-table'
import { useReportTypes } from '../root/hooks/useReportTypes'
import {
    urlToSearchState,
    useSyncUrlState
} from '../../utils/hooks/useSyncUrlState'
import { DeviceFilters } from '../root/components/common/device-filters/device-filters'
import { useDevicesFilters } from '../root/components/common/device-filters/use-device-filters'
import { prepareFilters } from '../root/components/common/device-filters/utils'
import { UserPermissions } from '../../services/permissions'
import { getFileForExport, getHotSwapFileForExport } from './api/device-api'
import classNames from 'classnames'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { enqueueSnackbar } from '../root/actions'
import { IconButton } from '@material-ui/core'
import { mdiTableArrowDown } from '@mdi/js'
import Icon from '@mdi/react'
import { ExportMenu } from '../root/components/common/export-menu'
import { useDevicesMultiselect } from '../root/hooks/use-devices-multiselect'
import { DevicesMultiselectMenu } from './components/devices-multiselect-menu'
import { X } from 'lucide-react'
import { MoveToLocationModal } from './move-to-location-modal'
import { DeleteDevicesModal } from './delete-devices-modal'
import { SwitchOffDevicesModal } from './switch-off-devices-modal'
import { SwitchOnMModeModal } from './switch-on-m-mode-modal'

export default function Devices() {
    const classes = useStyles()
    const { isLoading, locale } = useSelector(state => state.main)
    const theme = useSelector(state => state.main.theme)
    const { devices, isLoadingMore } = useSelector(state => state.devices)
    const [currentDeviceSerial, setCurrentDeviceSerial] = useState(null)
    const [isOpenQrModal, setIsOpenQrModal] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const location = useLocation()

    const { reportSeverityGroups } = useReportTypes()

    const [filters, d] = useDevicesFilters(
        urlToSearchState(location),
        UserPermissions.isAllOriginalGranted()
    )

    const preparedFilters = useMemo(() => {
        return prepareFilters(
            filters,
            reportSeverityGroups,
            UserPermissions.isAllOriginalGranted()
        )
    }, [filters, reportSeverityGroups])

    const {
        isRowSelected,
        selectRow,
        selectedRowsAmount,
        clearSelection,
        isMovingToLocationModalOpen,
        setIsMovingToLocationModalOpen,
        isDeleteDevicesModalOpen,
        setIsDeleteDevicesModalOpen,
        isSwitchOffModalOpen,
        setIsSwitchOffModalOpen,
        selectedDevicesIds,
        excludedDevicesIds,
        toggleAllSelector,
        isAllSelectorOn,
        isAllCheckboxIndeterminate,
        isMModeDevicesModalOpen,
        setIsMModeDevicesModalOpen
    } = useDevicesMultiselect(preparedFilters)

    useSyncUrlState(filters)
    const dispatch = useDispatch()

    const onQrIconClick = deviceSerial => {
        setCurrentDeviceSerial(deviceSerial)
        setIsOpenQrModal(true)
    }

    const exportDevices = async fileType => {
        setIsExporting(true)
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const processedFilters = prepareFilters(
            filters,
            reportSeverityGroups,
            UserPermissions.isAllOriginalGranted()
        )
        const params = { fileType, locale_key, ...processedFilters }
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('devices', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDevicesEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    const hotSwapExport = async () => {
        setIsExporting(true)

        const processedFilters = prepareFilters(
            filters,
            reportSeverityGroups,
            UserPermissions.isAllOriginalGranted()
        )
        const params = { ...processedFilters }
        const blob = await getHotSwapFileForExport(params)
        if (blob) {
            const fileName = composeFileName('hot-swap-devices', 'xlsx')
            downloadFile(blob, 'xlsx', fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDevicesEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }

        setIsExporting(false)
    }

    const loadMoreDevices = useCallback(
        debounce(() => {
            dispatch(
                actions.fetchMoreDevices(
                    prepareFilters(
                        filters,
                        reportSeverityGroups,
                        UserPermissions.isAllOriginalGranted()
                    )
                )
            )
        }, 300)
    )

    const initRequestDevices = preparedFilters => {
        dispatch(actions.devicesRequest(preparedFilters))
    }

    useEffect(() => {
        initRequestDevices(preparedFilters)
        return () => {
            clearSelection()
        }
    }, [preparedFilters])

    return (
        <React.Fragment>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div
                className={classNames(classes.wrapper, {
                    [`${classes.disabledWrapper}`]: isExporting
                })}>
                <PageTitle
                    title={locale.devices.devices}
                    className={classes.pageTitle}>
                    <div className={classes.filtersWrapper}>
                        <div className={classes.exportBtnsWrapper}>
                            {/* {UserPermissions.isQCUser() ? (
                                <Tooltip hover text={'Hot swap Export'}>
                                    <IconButton onClick={hotSwapExport}>
                                        <Icon
                                            path={mdiTableArrowDown}
                                            size={1}
                                        />
                                    </IconButton>
                                </Tooltip>
                            ) : null} */}
                            <ExportMenu>
                                {({ handleClose }) => (
                                    <>
                                        <ExportMenu.Csv
                                            locale={locale}
                                            onExport={fileType => {
                                                exportDevices(fileType)
                                                handleClose()
                                            }}
                                        />
                                        <ExportMenu.Excel
                                            locale={locale}
                                            onExport={fileType => {
                                                exportDevices(fileType)
                                                handleClose()
                                            }}
                                        />
                                        <ExportMenu.Pdf
                                            locale={locale}
                                            onExport={fileType => {
                                                exportDevices(fileType)
                                                handleClose()
                                            }}
                                        />
                                    </>
                                )}
                            </ExportMenu>
                        </div>
                    </div>
                </PageTitle>

                <div className={classes.filtersContainer}>
                    <div
                        style={{
                            marginBottom: 16,
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                        {selectedRowsAmount ? (
                            <>
                                <DevicesMultiselectMenu
                                    selectedRowsAmount={selectedRowsAmount}
                                    setIsMovingToLocationModalOpen={
                                        setIsMovingToLocationModalOpen
                                    }
                                    setIsDeleteDevicesModalOpen={
                                        setIsDeleteDevicesModalOpen
                                    }
                                    setIsSwitchOffModalOpen={
                                        setIsSwitchOffModalOpen
                                    }
                                    setIsMModeDevicesModalOpen={
                                        setIsMModeDevicesModalOpen
                                    }
                                />
                                <IconButton
                                    onClick={clearSelection}
                                    style={{ marginLeft: 12 }}
                                    size="small">
                                    <X color={theme.primary} size={18} />
                                </IconButton>
                            </>
                        ) : null}
                    </div>
                    <DeviceFilters
                        className={classes.deviceFilters}
                        locale={locale}
                        onChange={d}
                        filters={filters}
                    />
                </div>

                {/* {isLoading &&
                    <Loading />
                } */}

                {!isLoading && isEmptyList(devices) && (
                    <div>{locale.devices.noDevicesSetup}</div>
                )}

                {!isEmptyList(devices) && (
                    <div style={{ flex: 1 }}>
                        <DevicesTable
                            sorting={filters.sort_by}
                            onSort={sort_by => d({ type: 'sort', sort_by })}
                            isLoadingMore={isLoadingMore}
                            isLoading={isLoading}
                            onLoadMore={loadMoreDevices}
                            devices={devices}
                            onQrIconClick={onQrIconClick}
                            search={filters.search}
                            showAssignmentColumn
                            multiselect={{
                                isRowSelected,
                                selectRow,
                                toggleAllSelector,
                                isAllSelectorOn,
                                isAllCheckboxIndeterminate
                            }}
                        />
                    </div>
                )}

                <DeviceSerialQrCodeModal
                    deviceSerial={currentDeviceSerial}
                    open={isOpenQrModal}
                    setOpen={setIsOpenQrModal}
                />

                <MoveToLocationModal
                    open={isMovingToLocationModalOpen}
                    onClose={() => setIsMovingToLocationModalOpen(false)}
                    selectedDevicesIds={selectedDevicesIds}
                    excludedDevicesIds={excludedDevicesIds}
                    filters={preparedFilters}
                    clearSelection={clearSelection}
                    reloadDevices={initRequestDevices}
                />

                <DeleteDevicesModal
                    open={isDeleteDevicesModalOpen}
                    onClose={() => setIsDeleteDevicesModalOpen(false)}
                    selectedDevicesIds={selectedDevicesIds}
                    excludedDevicesIds={excludedDevicesIds}
                    filters={preparedFilters}
                    clearSelection={clearSelection}
                    reloadDevices={initRequestDevices}
                />

                <SwitchOffDevicesModal
                    open={isSwitchOffModalOpen}
                    onClose={() => setIsSwitchOffModalOpen(false)}
                    selectedDevicesIds={selectedDevicesIds}
                    excludedDevicesIds={excludedDevicesIds}
                    filters={preparedFilters}
                    clearSelection={clearSelection}
                    reloadDevices={initRequestDevices}
                />

                <SwitchOnMModeModal
                    open={isMModeDevicesModalOpen}
                    onClose={() => setIsMModeDevicesModalOpen(false)}
                    selectedDevicesIds={selectedDevicesIds}
                    excludedDevicesIds={excludedDevicesIds}
                    filters={preparedFilters}
                    clearSelection={clearSelection}
                    reloadDevices={initRequestDevices}
                />
            </div>
        </React.Fragment>
    )
}

const useStyles = makeStyles(theme => ({
    switcher: {
        marginRight: 12,
        marginBottom: 16
    },
    wrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    },
    icon: {
        color: theme.primary,
        cursor: 'pointer'
    },

    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    modalButton: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 15
    },
    pickerWrapper: {
        marginTop: 15
    },
    pageTitle: {
        justifyContent: 'space-between',
        height: 56
    },
    searchInput: {
        width: '35%'
    },

    input: {
        fontSize: 14,
        height: '32px',
        width: 150,
        borderRadius: '4px'
    },
    title: {
        marginBottom: 0
    },
    filterSelect: {
        marginRight: 16
    },
    statusFilterItem: {
        fontSize: 14
    },
    placeholderFilter: {
        color: theme.palette.text.secondary
    },
    filtersWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
    },
    saerchClearIcon: {
        padding: 0,
        position: 'absolute',
        right: 0
    },
    exportBtnsWrapper: {
        display: 'flex',
        gap: '6px',
        marginRight: '16px',
        alignItems: 'center'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    },
    filtersContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    exportIcon: {
        color: theme.quaternary
    }
}))
